import React, { useRef, useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.svg';
import CalendarSidebar from '../components/CalendarSidebar';
import { searchApi } from '../utils/api';

const LandingPage = () => {
    const { keycloak } = useKeycloak();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const aboutRef = useRef(null);
    const [todayNotesCount, setTodayNotesCount] = useState(0);
    const [overdueNotesCount, setOverdueNotesCount] = useState(0);

    useEffect(() => {
        if (keycloak?.authenticated) {
            fetchNoteAlerts();
        }
    }, [keycloak?.authenticated]);

    const fetchNoteAlerts = async () => {
        try {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const dateString = `${year}-${month}-${day}`;
            
            // 1. Notatki na dziś
            const resToday = await searchApi.get('', {
                params: {
                    targetFrom: `${dateString}T00:00`,
                    targetTo: `${dateString}T23:59`,
                    completed: false,
                    size: 1
                }
            });
            setTodayNotesCount(resToday.data.totalElements || 0);

            // 2. Notatki zaległe (termin < dziś 00:00)
            const resOverdue = await searchApi.get('', {
                params: {
                    targetTo: `${dateString}T00:00`,
                    completed: false,
                    size: 1
                }
            });
            setOverdueNotesCount(resOverdue.data.totalElements || 0);
        } catch (err) {
            console.error("Failed to fetch note alerts", err);
        }
    };

    const navigateToToday = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        navigate(`/advanced-search?targetFrom=${dateString}T00:00&targetTo=${dateString}T23:59&completed=false`);
    };

    const navigateToOverdue = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        navigate(`/advanced-search?targetTo=${dateString}T00:00&completed=false`);
    };

    const scrollToAbout = () => {
        aboutRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const name = keycloak?.idTokenParsed?.given_name || keycloak?.idTokenParsed?.preferred_username;

    return (
        <div className="landing-page bg-custom min-vh-100 animate-fade-in">
            {/* Hero Section */}
            <div className="hero-section bg-card border-bottom shadow-sm py-4 py-md-5 position-relative overflow-hidden">
                <div className="container py-4 text-center">
                    <div className="mb-4 animate-float">
                        <img src={logo} alt="Second Brain Logo" width="80" height="90" className="rounded-circle p-2 bg-custom shadow-sm border border-primary border-2 d-inline-block d-md-none" />
                        <img src={logo} alt="Second Brain Logo" width="120" height="140" className="rounded-circle p-2 bg-custom shadow-sm border border-primary border-2 d-none d-md-inline-block" />
                    </div>
                    <h1 className="display-4 display-md-3 fw-bold mb-3">
                        <span className="text-primary">{t('app_title')}</span>
                    </h1>
                    <p className="lead mb-4 mb-md-5 mx-auto fs-5 fs-md-4 px-3 opacity-85" style={{ maxWidth: '750px', color: 'var(--sb-text)' }}>
                        {t('app_subtitle')}
                    </p>
                    
                    {!keycloak.authenticated ? (
                        <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center px-4">
                            <button className="btn btn-primary btn-lg px-5 rounded-pill shadow-sm fw-bold transition-all hover-scale" onClick={() => keycloak.login()}>
                                {t('login')}
                            </button>
                            <button className="btn btn-outline-primary btn-lg px-5 rounded-pill shadow-sm fw-bold transition-all" onClick={scrollToAbout}>
                                {t('learn_more')}
                            </button>
                        </div>
                    ) : (
                        <div className="container mt-4">
                            <div className="row justify-content-center g-4">
                                <div className="col-md-6 col-lg-5">
                                    <div className="card h-100 border-0 shadow-lg rounded-4 p-4 p-md-5 bg-primary text-white d-flex flex-column justify-content-center text-start">
                                        <h2 className="mb-4 display-6" style={{ color: '#ffffff' }}>{t('welcome_user', { name })}</h2>
                                        
                                        {todayNotesCount > 0 && (
                                            <div className="alert bg-white bg-opacity-25 border-0 mb-3 animate-pulse cursor-pointer hover-alert" style={{ color: '#ffffff', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }} onClick={navigateToToday}>
                                                <div className="d-flex align-items-center gap-3">
                                                    <span className="fs-3 text-white">📅</span>
                                                    <div>
                                                        <div className="fw-bold text-white">{t('tasks_today')}</div>
                                                        <div className="small opacity-100 text-white">
                                                            <Trans 
                                                                i18nKey="tasks_today_count" 
                                                                count={todayNotesCount} 
                                                                components={{ bold: <strong /> }} 
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {overdueNotesCount > 0 && (
                                            <div className="alert bg-danger bg-opacity-50 border-0 mb-4 cursor-pointer hover-alert" style={{ color: '#ffffff' }} onClick={navigateToOverdue}>
                                                <div className="d-flex align-items-center gap-3">
                                                    <span className="fs-3">⚠️</span>
                                                    <div>
                                                        <div className="fw-bold" style={{ color: '#ffffff' }}>{t('tasks_overdue')}</div>
                                                        <div className="small opacity-90" style={{ color: '#ffffff' }}>
                                                            <Trans 
                                                                i18nKey="tasks_overdue_count" 
                                                                count={overdueNotesCount} 
                                                                components={{ bold: <strong /> }} 
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {todayNotesCount === 0 && overdueNotesCount === 0 && (
                                            <p className="lead mb-4 opacity-90">{t('welcome_back_message')}</p>
                                        )}

                                        <button className="btn btn-light btn-lg px-5 rounded-pill shadow-sm fw-bold text-primary w-100 w-sm-auto mt-auto" onClick={() => navigate('/dashboard')}>
                                            {t('go_to_notes')} →
                                        </button>
                                    </div>
                                </div>
                                <div className="col-md-6 col-lg-4 text-start">
                                    <CalendarSidebar />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Background Decoration (Floating Icons) */}
                <div className="position-absolute top-0 start-0 w-100 h-100 opacity-10" style={{ zIndex: -1, pointerEvents: 'none' }}>
                    <div className="position-absolute fs-1" style={{ top: '10%', left: '5%', transform: 'rotate(-15deg)' }}>📄</div>
                    <div className="position-absolute fs-1" style={{ top: '20%', right: '10%', transform: 'rotate(20deg)' }}>🧠</div>
                    <div className="position-absolute fs-1" style={{ bottom: '20%', left: '15%', transform: 'rotate(10deg)' }}>📎</div>
                    <div className="position-absolute fs-1" style={{ bottom: '10%', right: '15%', transform: 'rotate(-25deg)' }}>🏷️</div>
                </div>
            </div>

            {/* Architecture Visualization Section */}
            <div className="container py-5 mt-2 mt-md-4" ref={aboutRef}>
                <div className="row align-items-center g-4 g-lg-5">
                    <div className="col-lg-6 text-center text-lg-start">
                        <h2 className="display-6 display-md-5 fw-bold mb-4">{t('about_system_title')}</h2>
                        <p className="lead mb-4 lh-base lh-md-lg fs-6 fs-md-5 opacity-100" style={{ color: 'var(--sb-text)' }}>
                            {t('about_system_desc')}
                        </p>
                        <div className="d-flex gap-2 flex-wrap justify-content-center justify-content-lg-start mb-4">
                            <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-3 py-2">Elasticsearch</span>
                            <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-3 py-2">Apache Tika</span>
                            <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-3 py-2">Spring Boot</span>
                            <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-3 py-2">React</span>
                            <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-3 py-2">MinIO</span>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        {/* Visual Graph Diagram (CSS/SVG) */}
                        <div className="architecture-graph p-4 bg-card rounded-5 shadow-lg border d-flex justify-content-center align-items-center position-relative mx-auto" style={{ minHeight: '300px', maxWidth: '500px' }}>
                            <div className="central-brain animate-pulse">
                                <div className="brain-circle">🧠</div>
                            </div>
                            
                            {/* Satellite Nodes */}
                            <div className="sat-node note position-absolute" style={{ top: '10%', left: '15%' }}>
                                <div className="node-icon bg-info">📝</div>
                                <div className="node-label d-none d-sm-block">{t('graph_note')}</div>
                            </div>
                            <div className="sat-node pdf position-absolute" style={{ top: '10%', right: '15%' }}>
                                <div className="node-icon bg-danger">PDF</div>
                                <div className="node-label d-none d-sm-block">{t('graph_content')}</div>
                            </div>
                            <div className="sat-node tag position-absolute" style={{ bottom: '10%', left: '15%' }}>
                                <div className="node-icon bg-success">🏷️</div>
                                <div className="node-label d-none d-sm-block">{t('graph_tags')}</div>
                            </div>
                            <div className="sat-node search position-absolute" style={{ bottom: '10%', right: '15%' }}>
                                <div className="node-icon bg-warning">🔍</div>
                                <div className="node-label d-none d-sm-block">{t('graph_index')}</div>
                            </div>

                            {/* Connecting Lines (SVG) */}
                            <svg className="position-absolute w-100 h-100" style={{ pointerEvents: 'none' }}>
                                <line x1="50%" y1="50%" x2="25%" y2="25%" stroke="currentColor" strokeWidth="2" strokeDasharray="5,5" className="opacity-25" />
                                <line x1="50%" y1="50%" x2="75%" y2="25%" stroke="currentColor" strokeWidth="2" strokeDasharray="5,5" className="opacity-25" />
                                <line x1="50%" y1="50%" x2="25%" y2="75%" stroke="currentColor" strokeWidth="2" strokeDasharray="5,5" className="opacity-25" />
                                <line x1="50%" y1="50%" x2="75%" y2="75%" stroke="currentColor" strokeWidth="2" strokeDasharray="5,5" className="opacity-25" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* How It Works (Steps) */}
            <div className="bg-card py-5 border-top border-bottom">
                <div className="container py-4">
                    <h2 className="text-center fw-bold mb-5 fs-3 fs-md-2">{t('how_it_works_title')}</h2>
                    <div className="row g-3 g-md-4">
                        {[1, 2, 3, 4].map((step) => (
                            <div key={step} className="col-6 col-md-3">
                                <div className="text-center p-3 p-md-4 h-100 transition-all border-0 rounded-4">
                                    <div className="step-number bg-primary text-white rounded-circle mx-auto mb-3 shadow" style={{ width: '35px', height: '35px', lineHeight: '35px', fontSize: '0.9rem' }}>
                                        {step}
                                    </div>
                                    <h5 className="fw-bold fs-6 fs-md-5">{t(`step_${step}_title`)}</h5>
                                    <p className="opacity-100 small d-none d-sm-block text-muted">{t(`step_${step}_desc`)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Final CTA */}
            {!keycloak.authenticated && (
                <div className="container py-5 text-center">
                    <div className="p-4 p-md-5 rounded-5 bg-primary bg-gradient text-white shadow-lg mx-2">
                        <h2 className="fw-bold mb-4 fs-3 fs-md-2">{t('cta_title')}</h2>
                        <button className="btn btn-light btn-lg px-5 rounded-pill shadow fw-bold text-primary w-100 w-sm-auto" onClick={() => keycloak.login()}>
                            {t('cta_button')}
                        </button>
                    </div>
                </div>
            )}

            <style>{`
                .landing-page { scroll-behavior: smooth; }
                .bg-custom { background-color: var(--sb-bg); color: var(--sb-text); transition: all 0.3s ease; }
                .bg-card { background-color: var(--sb-card-bg); }
                .animate-float { animation: float 6s ease-in-out infinite; }
                .animate-pulse { animation: pulse 4s ease-in-out infinite; }
                .hover-scale:hover { transform: scale(1.05); }
                
                @keyframes float {
                    0% { transform: translateY(0px); }
                    50% { transform: translateY(-20px); }
                    100% { transform: translateY(0px); }
                }

                @keyframes pulse {
                    0% { transform: scale(1); filter: drop-shadow(0 0 0px var(--sb-primary)); }
                    50% { transform: scale(1.1); filter: drop-shadow(0 0 15px rgba(13, 110, 253, 0.5)); }
                    100% { transform: scale(1); filter: drop-shadow(0 0 0px var(--bs-primary)); }
                }

                .central-brain {
                    width: 70px;
                    height: 70px;
                    background: var(--sb-primary);
                    color: white;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 2rem;
                    z-index: 10;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                }

                @media (min-width: 768px) {
                    .central-brain {
                        width: 100px;
                        height: 100px;
                        font-size: 3rem;
                    }
                }

                .sat-node {
                    text-align: center;
                    transition: all 0.3s ease;
                }
                .sat-node:hover { transform: scale(1.1); }

                .node-icon {
                    width: 45px;
                    height: 45px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-weight: bold;
                    font-size: 1rem;
                    margin-bottom: 5px;
                    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
                }

                @media (min-width: 768px) {
                    .node-icon {
                        width: 60px;
                        height: 60px;
                        border-radius: 15px;
                        font-size: 1.2rem;
                    }
                }
                .node-label { font-size: 0.7rem; font-weight: bold; color: var(--sb-text); opacity: 0.9; }
                @media (min-width: 768px) {
                    .node-label { font-size: 0.8rem; }
                }

                .step-number { font-weight: bold; }
                .cursor-pointer { cursor: pointer; }
                .hover-alert:hover {
                    background-color: rgba(255, 255, 255, 0.3) !important;
                    transition: background-color 0.2s ease;
                }
                .alert.bg-danger.hover-alert:hover {
                    background-color: rgba(220, 53, 69, 0.7) !important;
                }
            `}</style>
        </div>
    );
};

export default LandingPage;
